import { SCHEDULE_NOTIFICATIONS } from "./player-communications-page";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/dark.css";
import { useColors } from "../../provider/colors-provider";
import { Fragment } from "react";
import RenderSendToContainer from "./RenderSendToContainer";

type Props = {
  dynamicTabs: Array<{
    name: string;
    value: string;
    icon: JSX.Element;
    description: string;
  }>;
  setCurrentTabSelected: (tab: string) => void;
  scheduledNotifications: string;
  setScheduledNotifications: (value: string) => void;
  curentTabSelected: string;
  date: Date;
  setDate: (date: Date) => void;
  refetch: () => void;
};

const RadioAndButtonGroupContainer = ({
  dynamicTabs,
  setCurrentTabSelected,
  scheduledNotifications,
  setScheduledNotifications,
  curentTabSelected,
  date,
  setDate,
  refetch,
}: Props) => {
  const { primary } = useColors();

  const TABS =
    scheduledNotifications === SCHEDULE_NOTIFICATIONS.FUTURE
      ? dynamicTabs.filter(
          (tab) => tab.value !== "single-user" && tab.value !== "csv-import"
        )
      : dynamicTabs;

  return (
    <div className="mb-10 max-w p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
      <h5 className="text-center mb-5 text-2xl font-normal tracking-tight text-gray-900">
        Create notifications
      </h5>
      <div className="flex gap-6 justify-center">
        <div
          onClick={() => setScheduledNotifications(SCHEDULE_NOTIFICATIONS.NOW)}
          style={{
            width: "16rem", // Fixed width to prevent layout shift
            ...(scheduledNotifications === SCHEDULE_NOTIFICATIONS.NOW
              ? {
                  backgroundColor: primary,
                  color: "white",
                  display: "flex", // Center content vertically and horizontally
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }
              : { border: `2px solid ${primary}`, backgroundColor: "white" }),
          }}
          className="hover:cursor-pointer hover:opacity-70 hover:shadow-2xl transition-all max-w-sm p-6 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700"
        >
          <h5 className="text-center text-2xl font-bold tracking-tight">
            Send Now
          </h5>
          {!scheduledNotifications && (
            <p className="text-center mb-3 font-normal text-gray-700 dark:text-gray-400">
              Choosing this option will send the notification immediately
            </p>
          )}
        </div>

        <div
          onClick={() =>
            setScheduledNotifications(SCHEDULE_NOTIFICATIONS.FUTURE)
          }
          style={{
            width: "16rem", // Fixed width to prevent layout shift
            ...(scheduledNotifications === SCHEDULE_NOTIFICATIONS.FUTURE
              ? {
                  backgroundColor: primary,
                  color: "white",
                  display: "flex", // Center content vertically and horizontally
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }
              : { border: `2px solid ${primary}`, backgroundColor: "white" }),
          }}
          className="hover:cursor-pointer hover:opacity-70 hover:shadow-2xl transition-all max-w-sm p-6 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700"
        >
          <h5 className="text-center text-2xl font-bold tracking-tight">
            Schedule
          </h5>
          {!scheduledNotifications && (
            <p className="text-center mb-3 font-normal text-gray-700 dark:text-gray-400">
              Choosing this option will send the notification at a later date
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex justify-center flex-col items-center">
        {scheduledNotifications === SCHEDULE_NOTIFICATIONS.FUTURE && (
          <Fragment>
            <p className="text-center mb-3 font-normal text-gray-700 dark:text-gray-400">
              Choose the date and time to send the notification below
            </p>
            <div
              style={{
                border: `2px solid ${primary}`,
              }}
              className="hover:cursor-pointer hover:opacity-70 hover:shadow-2xl border w-max rounded-full  text-center p-2 border-gray-200 dark:border-gray-700"
            >
              <Flatpickr
                className="hover:cursor-pointer border-gray-200 rounded-full w-72 p-2 text-center outline-none"
                options={{
                  enableTime: true,
                  altInput: true,
                  altFormat: "d F Y h:i K",
                  dateFormat: "u",
                  minDate: "today",
                }}
                data-enable-time
                value={date}
                onChange={([date]) => {
                  // Prevent selecting a date in the past
                  const now = new Date();
                  if (date < now) {
                    setDate(now);
                  } else {
                    setDate(date);
                  }
                }}
              />
            </div>
          </Fragment>
        )}
      </div>
      {scheduledNotifications && (
        <div
          style={{
            opacity: !scheduledNotifications ? 0.5 : 1,
          }}
        >
          <h5 className="text-center mt-4 text-2xl font-normal tracking-tight text-gray-900 dark:text-white">
            Select Which Recipients to Notify
          </h5>
          <div className="mt-5 flex gap-6 justify-center">
            {TABS.map((tab, index) => (
              <Fragment>
                <div
                  key={index}
                  onClick={() => {
                    if (!scheduledNotifications) return;
                    setCurrentTabSelected(tab.value);
                  }}
                  style={{
                    border:
                      curentTabSelected === tab.value
                        ? `2px solid ${primary}`
                        : "",
                  }}
                  className="text-center hover:cursor-pointer hover:opacity-70 hover:shadow-2xl transition-all max-w-sm p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700"
                >
                  <span className="flex gap-4 items-center justify-center">
                    <h5 className="text-center text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {tab.name}
                    </h5>
                    <span>{tab.icon}</span>
                  </span>
                  <p className="text-center mt-3 font-normal text-gray-700 dark:text-gray-400">
                    {tab.description}
                  </p>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {curentTabSelected && (
        <div className="mt-6">
          <h5 className="mt-10 text-lg font-bold tracking-tight text-gray-900 dark:text-white">
            Send Notification to
          </h5>
          <RenderSendToContainer
            currentSelectedTab={curentTabSelected}
            scheduleNotifications={scheduledNotifications}
            notificationScheduleTime={date}
            refetch={refetch}
          />
        </div>
      )}
    </div>
  );
};

export default RadioAndButtonGroupContainer;
